import React, { useState } from "react";
import { Navbar } from "components/layout/components/Navbar";
import RecruitmentStatusScreen from "./RecruitmentStatusScreen";
import TalentOutcomeScreen from "./TalentOutcomeScreen";
import HiredTalentsScreen from "./HiredTalentsScreen";
import {
  IFeedbackJob,
  FlowStep,
  FeedbackData,
  TalentOutcome,
} from "../types";
import { useNavigate } from "react-router-dom";
import { useSavePendingJobsFeedback } from "../hooks/useSavePendingJobsFeedback";
import { useGetUserInfo } from "hooks/useGetUserInfo";

interface RecruitmentFeedbackFlowProps {
  job: IFeedbackJob | null;
  isLoading: boolean;
  onExit: () => void;
  onCompleted: (talents: string[]) => void;
}

const RecruitmentFeedbackFlow: React.FC<RecruitmentFeedbackFlowProps> = ({
  job,
  isLoading,
  onExit,
  onCompleted,
}) => {
  const [step, setStep] = useState<FlowStep>("RECRUITMENT_STATUS");
  const [feedback, setFeedback] = useState<FeedbackData>({
    recruitmentStatus: "",
    talentOutcome: "",
    hiredTalents: [],
  });

  const navigate = useNavigate();

  const { token } = useGetUserInfo();
  const { mutate: savePendingJobsFeedback } = useSavePendingJobsFeedback();
  if (isLoading || !job) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  const handleSavePendingJobsFeedback = (payload: {
    jobId?: string;
    talents?: string[];
    token?: string;
  }) => {
    savePendingJobsFeedback(payload, {
      onSuccess: () => navigate("/"),
    });
  };

  const handleTalentOutcomeContinue = (value: TalentOutcome) => {
    setFeedback((prev) => ({ ...prev, talentOutcome: value }));
    if (value !== "platform") {
      handleSavePendingJobsFeedback({
        jobId: job.id,
        talents: [],
        token
      });
    } else {
      setStep("HIRED_TALENTS");
    }
  };

  const handleHiredTalentsSubmit = (talents: string[]) => {
    const payload = {
      ...feedback,
      hiredTalents: talents,
    };
    onCompleted(payload.hiredTalents);
  };

  return (
    <>
      <Navbar
        title="Recruitment Feedback"
        description="Provide feedback on closed jobs"
      />

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {step === "RECRUITMENT_STATUS" && (
          <RecruitmentStatusScreen
            job={job}
            value={feedback.recruitmentStatus}
            onValueChange={(value) =>
              setFeedback((f) => ({ ...f, recruitmentStatus: value }))
            }
            onContinue={(value) => {
              setFeedback((f) => ({ ...f, recruitmentStatus: value }));
              if (value == 'completed') {
                setStep("TALENT_OUTCOME");
              } else if (value == 'cancelled') {
                // call api to close the process
                handleSavePendingJobsFeedback({
                  jobId: job.id,
                  talents: [],
                  token
                });
                navigate('/');
              }
              else {
                navigate('/dashboard/skipRecruitmentFeedback')
              }
            }}
            onSkip={onExit}
          />
        )}

        {step === "TALENT_OUTCOME" && (
          <TalentOutcomeScreen
            job={job}
            value={feedback.talentOutcome}
            onValueChange={(value) =>
              setFeedback((f) => ({ ...f, talentOutcome: value }))
            }
            onBack={() => setStep("RECRUITMENT_STATUS")}
            onContinue={handleTalentOutcomeContinue}
          />
        )}

        {step === "HIRED_TALENTS" && (
          <HiredTalentsScreen
            job={job}
            onBack={() => setStep("TALENT_OUTCOME")}
            onSubmit={handleHiredTalentsSubmit}
          />
        )}
      </div>
    </>
  );
};

export default RecruitmentFeedbackFlow;
